import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext({});

// Detectar ambiente e configurar URL base
// Detectar ambiente e configurar URL base
const getApiUrl = () => {
  const isProduction = import.meta.env.VITE_IS_PRODUCTION === 'true';
  
  console.log('🔍 Detectando ambiente:', {
    isProduction,
    VITE_IS_PRODUCTION: import.meta.env.VITE_IS_PRODUCTION,
    VITE_API_URL: import.meta.env.VITE_API_URL
  });
  
  if (isProduction) {
    // Em produção, usar path relativo (Apache faz proxy)
    return '/api';
  }
  
  // Em desenvolvimento, usar URL completa
  const devUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  
  // Garantir que NÃO termina com /api (será adicionado nas rotas)
  return devUrl.replace(/\/api$/, '');
};

// Criar instância do axios para autenticação
const API_BASE_URL = getApiUrl();
console.log('🌐 API Base URL configurada:', API_BASE_URL);

const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000 // 10 segundos de timeout
});

// Interceptor para logs
authApi.interceptors.request.use(
  (config) => {
    const fullUrl = `${config.baseURL}${config.url}`;
    console.log(`🌐 Request: ${config.method?.toUpperCase()} ${fullUrl}`);
    console.log('📦 Data:', config.data);
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

authApi.interceptors.response.use(
  (response) => {
    console.log(`✅ Response: ${response.status}`, response.data);
    return response;
  },
  (error) => {
    console.error('❌ Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [usuario, setUsuario] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [ultimoAtividade, setUltimoAtividade] = useState(Date.now());

  // Configuração do timeout (30 minutos)
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutos em milissegundos

  // Verificar se já existe token ao carregar
  useEffect(() => {
    console.log('🔍 Verificando autenticação inicial...');
    verificarAutenticacao();
  }, []);

  // Atualizar timestamp de atividade
  const atualizarAtividade = useCallback(() => {
    setUltimoAtividade(Date.now());
  }, []);

  // Efeito para monitorar inatividade
  useEffect(() => {
    if (!usuario) return;

    const interval = setInterval(() => {
      const agora = Date.now();
      const tempoInativo = agora - ultimoAtividade;

      if (tempoInativo >= SESSION_TIMEOUT) {
        console.log('⏰ Session timeout atingido. Desconectando usuário...');
        logout();
        // Mostrar mensagem de timeout
        if (typeof window !== 'undefined' && window.alert) {
          setTimeout(() => {
            alert('Sessão expirada por inatividade. Você foi desconectado automaticamente.');
          }, 100);
        }
      }
    }, 1000); // Verificar a cada segundo

    return () => clearInterval(interval);
  }, [usuario, ultimoAtividade, SESSION_TIMEOUT]);

  // Efeito para escutar eventos de atividade do usuário
  useEffect(() => {
    if (!usuario) return;

    const eventos = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleAtividade = () => {
      atualizarAtividade();
    };

    eventos.forEach(evento => {
      document.addEventListener(evento, handleAtividade, { passive: true });
    });

    // Verificar atividade periodicamente também
    const activityInterval = setInterval(() => {
      // Esta verificação adicional ajuda a garantir que o timestamp seja atualizado
      // mesmo que os eventos não sejam disparados em alguns cenários específicos
    }, 60000); // Verificar a cada minuto

    return () => {
      eventos.forEach(evento => {
        document.removeEventListener(evento, handleAtividade);
      });
      clearInterval(activityInterval);
    };
  }, [usuario, atualizarAtividade]);

  const verificarAutenticacao = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.log('⚠️ Nenhum token encontrado');
        setCarregando(false);
        return;
      }

      console.log('🔑 Token encontrado, verificando validade...');

      // Configurar token no axios
      authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Verificar se token é válido
      const isProduction = import.meta.env.VITE_IS_PRODUCTION === 'true';
      const authPath = isProduction ? '/auth/verificar' : '/api/auth/verificar';
      const response = await authApi.get(authPath);
      
      if (response.data.sucesso) {
        console.log('✅ Token válido, usuário autenticado:', response.data.dados.usuario);
        setUsuario(response.data.dados.usuario);
      } else {
        console.log('⚠️ Token inválido');
        logout();
      }
    } catch (error) {
      console.error('❌ Erro ao verificar autenticação:', error);
      logout();
    } finally {
      setCarregando(false);
    }
  };

  const login = async (username, senha) => {
    try {
      setErro(null);
      console.log('🔐 Iniciando processo de login...');
      console.log('👤 Username:', username);
      console.log('🌐 API URL:', getApiUrl());
      
      const isProduction = import.meta.env.VITE_IS_PRODUCTION === 'true';
      const authPath = isProduction ? '/auth/login' : '/api/auth/login';
      const response = await authApi.post(authPath, { 
        username, 
        senha 
      });
      
      console.log('📨 Resposta completa do servidor:', response);

      if (response.data.sucesso) {
        const { token, usuario: usuarioData } = response.data.dados;
        
        if (!token) {
          console.error('❌ Token não recebido do servidor');
          setErro('Erro ao receber token de autenticação');
          return { sucesso: false, mensagem: 'Token não recebido' };
        }

        if (!usuarioData) {
          console.error('❌ Dados do usuário não recebidos');
          setErro('Erro ao receber dados do usuário');
          return { sucesso: false, mensagem: 'Dados do usuário não recebidos' };
        }
        
        console.log('💾 Salvando token no localStorage...');
        localStorage.setItem('token', token);
        
        console.log('🔧 Configurando header de autorização...');
        authApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        console.log('👤 Salvando dados do usuário:', usuarioData);
        setUsuario(usuarioData);
        
        console.log('✅ Login concluído com sucesso!');
        return { sucesso: true };
      } else {
        const mensagem = response.data.mensagem || 'Erro ao fazer login';
        console.log('⚠️ Login não teve sucesso:', mensagem);
        setErro(mensagem);
        return { sucesso: false, mensagem };
      }
    } catch (error) {
      console.error('❌ ERRO NO LOGIN:');
      console.error('Tipo:', error.name);
      console.error('Mensagem:', error.message);
      console.error('Código:', error.code);
      
      if (error.response) {
        console.error('Response Status:', error.response.status);
        console.error('Response Data:', error.response.data);
        console.error('Response Headers:', error.response.headers);
      } else if (error.request) {
        console.error('Request enviada mas sem resposta:', error.request);
      }
      
      let mensagem = 'Erro ao fazer login';
      
      if (error.code === 'ECONNREFUSED') {
        mensagem = 'Não foi possível conectar ao servidor. Verifique se o backend está rodando.';
      } else if (error.code === 'ECONNABORTED') {
        mensagem = 'Tempo de conexão esgotado. Tente novamente.';
      } else if (error.response?.status === 404) {
        mensagem = 'Rota de login não encontrada. Verifique a configuração do backend.';
      } else if (error.response?.data?.mensagem) {
        mensagem = error.response.data.mensagem;
      }
      
      setErro(mensagem);
      return { sucesso: false, mensagem };
    }
  };

  const logout = async () => {
    console.log('🚪 Fazendo logout...');
    try {
      const isProduction = import.meta.env.VITE_IS_PRODUCTION === 'true';
      const authPath = isProduction ? '/auth/logout' : '/api/auth/logout';
      await authApi.post(authPath);
      console.log('✅ Logout no servidor concluído');
    } catch (error) {
      console.error('⚠️ Erro ao fazer logout no servidor:', error);
    } finally {
      console.log('🧹 Limpando dados locais...');
      localStorage.removeItem('token');
      delete authApi.defaults.headers.common['Authorization'];
      setUsuario(null);
      console.log('✅ Logout local concluído');
    }
  };

  const isAdmin = () => {
    return usuario?.perfil === 'admin';
  };

  const isGerenteOuAdmin = () => {
    return usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
  };

  const isAuthenticated = () => {
    const auth = !!usuario;
    return auth;
  };

  return (
    <AuthContext.Provider
      value={{
        usuario,
        carregando,
        erro,
        login,
        logout,
        isAdmin,
        isGerenteOuAdmin,
        isAuthenticated,
        setErro
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  return context;
};

export default AuthContext;